import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { ScrollArea } from '../../../components/ui/scroll-area';
import {
  FileText,
  Download,
  ExternalLink,
  Loader2,
  Eye,
  Image,
  FileCode,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * PreviewModal - Document Preview Modal
 * 
 * Supports previewing:
 * - PDFs (embedded viewer)
 * - Images (PNG, JPG, JPEG, GIF, WEBP, SVG)
 * - Word Documents (.docx, .doc) - extracts text content
 * - Text files (.txt, .md, .csv, .json, .xml, etc.)
 */
const PreviewModal = ({
  isOpen,
  onClose,
  document,
  previewContent,
  loading,
  getAuthHeaders,
  onDownload,
}) => {
  const getFileIcon = (fileType) => {
    const type = fileType?.toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(type)) {
      return <Image className="w-5 h-5 text-green-400" />;
    }
    if (['pdf'].includes(type)) {
      return <FileText className="w-5 h-5 text-red-400" />;
    }
    if (['docx', 'doc'].includes(type)) {
      return <FileText className="w-5 h-5 text-blue-400" />;
    }
    if (['txt', 'md', 'csv', 'json', 'xml', 'html', 'css', 'js', 'py'].includes(type)) {
      return <FileCode className="w-5 h-5 text-amber-400" />;
    }
    return <FileText className="w-5 h-5 text-gray-500" />;
  };

  const getFileBadgeColor = (fileType) => {
    const type = fileType?.toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(type)) {
      return 'bg-green-500/20 text-green-400 border-green-500/30';
    }
    if (['pdf'].includes(type)) {
      return 'bg-red-500/20 text-red-400 border-red-500/30';
    }
    if (['docx', 'doc'].includes(type)) {
      return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    }
    return 'bg-slate-500/20 text-gray-500 border-slate-500/30';
  };

  const isImage = () => {
    const type = document?.file_type?.toLowerCase();
    return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(type);
  };

  const isPDF = () => {
    return document?.file_type?.toLowerCase() === 'pdf';
  };

  const isWordDoc = () => {
    const type = document?.file_type?.toLowerCase();
    return ['docx', 'doc'].includes(type);
  };

  const getPreviewUrl = () => {
    if (previewContent?.content) {
      return `data:${previewContent.type || 'application/octet-stream'};base64,${previewContent.content}`;
    }
    return null;
  };

  const renderPreviewContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
        </div>
      );
    }

    if (!previewContent) {
      return (
        <div className="flex flex-col items-center justify-center h-96 text-gray-500">
          <Eye className="w-12 h-12 mb-4 opacity-50" />
          <p>No preview available</p>
        </div>
      );
    }

    // Word Document Preview
    if (isWordDoc() && previewContent.paragraphs) {
      return (
        <ScrollArea className="h-[500px]">
          <div className="p-4 space-y-4">
            <div className="flex items-center gap-2 text-sm text-gray-500 pb-2 border-b border-gray-200">
              <FileText className="w-4 h-4" />
              Word Document - {previewContent.paragraph_count || 0} paragraphs, {previewContent.table_count || 0} tables
            </div>
            <div className="space-y-3">
              {previewContent.paragraphs?.map((para, idx) => (
                <p key={idx} className="text-gray-600 text-sm leading-relaxed">
                  {para || '\u00A0'}
                </p>
              ))}
            </div>
            {previewContent.tables?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-500 mb-2">Tables:</p>
                {previewContent.tables.map((table, tIdx) => (
                  <div key={tIdx} className="overflow-x-auto mb-4">
                    <table className="min-w-full border border-gray-200 text-sm">
                      <tbody>
                        {table.map((row, rIdx) => (
                          <tr key={rIdx} className={rIdx % 2 === 0 ? 'bg-gray-50/80' : 'bg-gray-50/30'}>
                            {row.map((cell, cIdx) => (
                              <td key={cIdx} className="px-3 py-2 border border-gray-200 text-gray-600">
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          </div>
        </ScrollArea>
      );
    }

    // PDF Preview
    if (isPDF()) {
      const pdfUrl = getPreviewUrl();
      return (
        <div className="h-[500px]">
          {pdfUrl ? (
            <iframe
              src={pdfUrl}
              className="w-full h-full rounded-lg border border-gray-200"
              title={`Preview of ${document?.name}`}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <FileText className="w-12 h-12 mb-4 opacity-50" />
              <p>Unable to load PDF preview</p>
            </div>
          )}
        </div>
      );
    }

    // Image Preview
    if (isImage()) {
      const imageUrl = getPreviewUrl();
      return (
        <div className="flex items-center justify-center h-[500px] bg-gray-50/30 rounded-lg">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={document?.name}
              className="max-h-full max-w-full object-contain rounded-lg"
            />
          ) : (
            <div className="flex flex-col items-center justify-center text-gray-500">
              <Image className="w-12 h-12 mb-4 opacity-50" />
              <p>Unable to load image preview</p>
            </div>
          )}
        </div>
      );
    }

    // Text Content Preview
    if (previewContent.text_content || previewContent.content) {
      return (
        <ScrollArea className="h-[500px]">
          <pre className="p-4 bg-gray-50/80 rounded-lg text-sm text-gray-600 whitespace-pre-wrap font-mono">
            {previewContent.text_content || atob(previewContent.content)}
          </pre>
        </ScrollArea>
      );
    }

    // Default - no preview
    return (
      <div className="flex flex-col items-center justify-center h-96 text-gray-500">
        <Eye className="w-12 h-12 mb-4 opacity-50" />
        <p>Preview not available for this file type</p>
        <Button
          variant="outline"
          size="sm"
          onClick={onDownload}
          className="mt-4"
        >
          <Download className="w-4 h-4 mr-2" />
          Download to view
        </Button>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden bg-white border-gray-200 text-gray-900">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-900">
            {getFileIcon(document?.file_type)}
            {document?.name}
            <Badge className={getFileBadgeColor(document?.file_type)}>
              {document?.file_type?.toUpperCase()}
            </Badge>
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            {document?.document_type && (
              <span>Type: {document.document_type} • </span>
            )}
            Version {document?.version || 1}
            {document?.status && (
              <span> • Status: {document.status.replace(/_/g, ' ')}</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0">
          {renderPreviewContent()}
        </div>

        <DialogFooter className="border-t border-gray-200 pt-4">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={onDownload}
              className="rounded-full bg-blue-600 border-blue-500 text-gray-900 hover:bg-blue-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
            {isPDF() && getPreviewUrl() && (
              <Button
                variant="outline"
                onClick={() => window.open(getPreviewUrl(), '_blank')}
                className="rounded-full bg-slate-700 border-gray-300 text-gray-900 hover:bg-slate-600"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open in New Tab
              </Button>
            )}
            <Button
              variant="outline"
              onClick={onClose}
              className="rounded-full bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            >
              Close
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PreviewModal;
